import { useCallback, useEffect, useRef, useState } from 'react';

type SelfieStatus = 'idle' | 'starting' | 'ready' | 'error';

type UseSelfieCameraResult = {
  stream: MediaStream | null;
  videoTrack: MediaStreamTrack | null;
  audioTrack: MediaStreamTrack | null;
  status: SelfieStatus;
  error: string | null;
  isSupported: boolean;
  start: () => Promise<MediaStream | null>;
  stop: () => void;
};

const SELFIE_CONSTRAINTS: MediaStreamConstraints = {
  video: {
    facingMode: 'user',
    width: { ideal: 480 },
    height: { ideal: 640 },
    frameRate: { ideal: 15, max: 20 },
  },
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
};

export const useSelfieCamera = (): UseSelfieCameraResult => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [status, setStatus] = useState<SelfieStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const isSupported = typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    streamRef.current = null;
    setStream(null);
    setStatus('idle');
  }, []);

  const start = useCallback(async () => {
    if (!isSupported) {
      setError('camera_unavailable');
      setStatus('error');
      return null;
    }

    if (streamRef.current) {
      return streamRef.current;
    }

    setStatus('starting');
    setError(null);

    try {
      const nextStream = await navigator.mediaDevices.getUserMedia(SELFIE_CONSTRAINTS);
      streamRef.current = nextStream;
      setStream(nextStream);
      setStatus('ready');
      return nextStream;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'camera_error';
      setError(message);
      setStatus('error');
      return null;
    }
  }, [isSupported]);

  useEffect(() => () => stop(), [stop]);

  return {
    stream,
    videoTrack: stream?.getVideoTracks()[0] ?? null,
    audioTrack: stream?.getAudioTracks()[0] ?? null,
    status,
    error,
    isSupported,
    start,
    stop,
  };
};
